import { useCallback, useEffect, useRef, useState } from 'react';

import {
  BlockedWorkspaceSwitch,
  consumeBlockedWorkspaceSwitch,
  consumeWorkspaceSwitchPin,
  isPinnedIdentity,
  rememberBlockedWorkspaceSwitch,
} from '../lib/workspace-switch-identity';

type UseWorkspaceSwitchGuardOptions = {
  /** Email of the resolved session, null when nobody is signed in. */
  email: string | null | undefined;
  /** True while auth is still resolving. Markers are read once it settles. */
  isLoading: boolean;
  signOut: () => Promise<void>;
};

/**
 * CLOUD-715. Checks the identity a workspace switch returns against the one that
 * started it. On a mismatch it signs out at once: the session cookie already
 * belongs to the other account, so there is no way back to the first one.
 *
 * Both markers are read on the first settled auth resolution, signed in or not.
 * A pin that outlived its own flow would otherwise be compared against a later,
 * unrelated login.
 */
export function useWorkspaceSwitchGuard({ email, isLoading, signOut }: UseWorkspaceSwitchGuardOptions) {
  const [blockedSwitch, setBlockedSwitch] = useState<BlockedWorkspaceSwitch | null>(null);
  const [isBlockingSwitch, setIsBlockingSwitch] = useState(false);
  const settledRef = useRef(false);

  useEffect(() => {
    if (isLoading || settledRef.current) return;
    settledRef.current = true;

    const pin = consumeWorkspaceSwitchPin();

    // Set just before the forced sign-out below, so this load is the return leg.
    const note = consumeBlockedWorkspaceSwitch();
    if (note) {
      setBlockedSwitch(note);
      return;
    }

    // No pin means no switch was in flight. No email means the switch did not
    // land on a session, so there is nothing to compare and nothing to refuse.
    if (!pin || !email || isPinnedIdentity(pin, email)) return;

    const blocked: BlockedWorkspaceSwitch = { expectedEmail: pin.email, actualEmail: email };
    console.warn('[ari-cloud] Workspace switch returned a different account — signing out');
    rememberBlockedWorkspaceSwitch(blocked);
    setIsBlockingSwitch(true);
    void signOut();
  }, [email, isLoading, signOut]);

  const dismissBlockedSwitch = useCallback(() => setBlockedSwitch(null), []);

  return { blockedSwitch, isBlockingSwitch, dismissBlockedSwitch };
}
